// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BankUploadSummaryRow } from '@client/banks/components/shared/bank-upload-summary-list';
import summaryListStyles from '@client/banks/components/shared/bank-upload-summary-list/bank-upload-summary-list.module.css';
import type { BankTransaction } from '@client/banks/types/transaction';
import { Typography } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { FC } from 'react';
import { TransactionsListHeader } from './transactions-list-header';
import styles from './transactions-list.module.css';

export type TransactionsListHeaderProps = {
  onAdd: () => void;
  onImportExcel?: () => void;
};

export type TransactionSummaryItem = {
  index: number;
  transaction: BankTransaction;
  title: string;
  meta: string;
  amountLabel: string;
};

export type TransactionsListProps = {
  items: TransactionSummaryItem[];
  isEmpty: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onImportExcel?: () => void;
};

export const TransactionsList: FC<TransactionsListProps> = ({
  items,
  isEmpty,
  onAdd,
  onEdit,
  onDelete,
  onImportExcel,
}) => (
  <div
    className="flex min-h-0 flex-1 flex-col"
    data-testid="bank-upload-transactions-list"
  >
    <TransactionsListHeader onAdd={onAdd} onImportExcel={onImportExcel} />

    <section className={styles['transactions-summary']} aria-live="polite">
      <ul className={summaryListStyles['summary-list']}>
        {isEmpty ? (
          <li
            className={summaryListStyles['summary-list-empty']}
            data-testid="bank-upload-transactions-list-empty"
          >
            <Typography
              variant="body2"
              className={summaryListStyles['summary-list-empty-message']}
            >
              <Translation
                i18nKey="aggregator.create-bank.data-section.empty-list-hint"
                ns="aggregator"
              />
            </Typography>
          </li>
        ) : (
          items.map(item => (
            <BankUploadSummaryRow
              key={`${item.transaction.iban}-${item.transaction.transactionDate}-${item.index}`}
              testIdPrefix="bank-upload-transaction"
              index={item.index}
              title={item.title}
              meta={item.meta}
              valueLabel={item.amountLabel}
              onOpen={onEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </ul>
    </section>
  </div>
);

TransactionsList.displayName = 'TransactionsList';
